import React, { useState, useEffect, useCallback, useRef } from 'react';

const C = { pos:'#389e0d', neg:'#cf1322', neu:'#8c8c8c', amber:'#ad6800' };
const sColor = s => s === 'POSITIVE' ? C.pos : s === 'NEGATIVE' ? C.neg : C.neu;

const inp = {
  fontSize: 12, padding: '6px 8px', borderRadius: 6,
  border: '1px solid var(--bd)', background: 'var(--bg1)',
  color: 'var(--t1)', width: '100%'
};
const lbl = { fontSize: 10, color: 'var(--t2)', textTransform: 'uppercase',
              letterSpacing: 0.3, marginBottom: 3, display: 'block' };

const NewsManual = () => {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sugg, setSugg] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const suggTimer = useRef(null);
  const suggSeq = useRef(0);
  const [f, setF] = useState({
    ticker: '', headline: '', sentiment: 'NEGATIVE',
    category: 'EARNINGS_DOWN', impact: 'MEDIUM', detail: '', source: ''
  });

  const load = useCallback(() => {
    return fetch('/news-manual/list?days=60')
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (j) setRows(j.rows || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/news-manual/meta').then(r => r.json()).then(setMeta).catch(() => {});
    load();
  }, [load]);

  const submit = async () => {
    if (saving) return;                       // 21 duplicate rows on 2026-08-02
    if (!f.ticker.trim() || !f.headline.trim()) {
      setMsg({ ok: false, text: 'Ticker and headline are required' });
      return;
    }
    setSaving(true);
    try {
      const r = await fetch('/news-manual/add', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(f)
      });
      const j = await r.json();
      if (r.ok) {
        setMsg({ ok: true, text: `Saved ${j.news.ticker} — expires ${j.news.expires_on}` });
        setF({ ticker: '', headline: '', sentiment: 'NEGATIVE',
               category: 'EARNINGS_DOWN', impact: 'MEDIUM', detail: '', source: '' });
        // reset typeahead too — leaving sugg/seq dirty made the next lookup
        // get dropped by the stale-response guard (2026-08-02)
        setSugg([]); setShowSugg(false);   // do NOT reset suggSeq — it must climb
                                           // monotonically or in-flight requests
                                           // desync the stale-response guard
        if (suggTimer.current) { clearTimeout(suggTimer.current); suggTimer.current = null; }
        load();   // fire-and-forget: don't hold the button on the list refresh
      } else {
        setMsg({ ok: false, text: j.detail || 'Failed' });
      }
    } catch (e) { setMsg({ ok: false, text: String(e) }); }
    finally { setSaving(false); }
  };

  const del = async id => {
    try {
      const r = await fetch('/news-manual/' + id, { method: 'DELETE' });
      if (!r.ok) { setMsg({ ok: false, text: 'Delete failed (' + r.status + ')' }); return; }
      await load();   // authoritative refetch; optimistic filter raced the reset
    } catch (e) { setMsg({ ok: false, text: 'Delete error: ' + e }); }
  };

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Typeahead: search by ticker OR company name so you don't have to look up codes.
  const onTicker = v => {
    set('ticker', v);
    if (suggTimer.current) clearTimeout(suggTimer.current);
    if (v.length < 2) { setSugg([]); setShowSugg(false); return; }
    // 180ms debounce + sequence guard: without these, fast typing fires a request
    // per keystroke and whichever resolves last wins, so results looked random.
    const seq = ++suggSeq.current;
    suggTimer.current = setTimeout(() => {
      fetch('/news-manual/tickers?q=' + encodeURIComponent(v))
        .then(r => r.ok ? r.json() : null)
        .then(j => {
          if (!j || seq !== suggSeq.current) return;   // stale response, drop it
          setSugg(j.rows || []);
          setShowSugg(true);
        })
        .catch(() => {});
    }, 180);
  };
  const pick = t => { set('ticker', t.ticker); setSugg([]); setShowSugg(false); };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Manual News</div>
      <div style={{ fontSize: 11, color: 'var(--t2)', marginBottom: 16 }}>
        Your own tags. Display only — nothing here affects entries or scoring.
        The automated classifier tagged 158 of 158 items POSITIVE, so this is the trustworthy input.
      </div>

      <div style={{ background:'var(--bg1)', border:'1px solid var(--bd)',
                    borderRadius:8, padding:16, marginBottom:20 }}>
        <div style={{ display:'grid', gridTemplateColumns:'140px 1fr', gap:10, marginBottom:10 }}>
          <div style={{ position:'relative' }}><label style={lbl}>Ticker</label>
            <input style={inp} value={f.ticker} placeholder="type name or code"
                   autoComplete="off"
                   onChange={e => onTicker(e.target.value.toUpperCase())}
                   onBlur={() => setTimeout(() => setShowSugg(false), 180)} />
            {showSugg && sugg.length > 0 && (
              <div style={{ position:'absolute', zIndex:20, top:'100%', left:0,
                            minWidth:320, maxHeight:240, overflowY:'auto',
                            background:'var(--bg1)', border:'1px solid var(--bd)',
                            borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.12)' }}>
                {sugg.map(t => (
                  <div key={t.ticker} onMouseDown={() => pick(t)}
                       style={{ padding:'6px 10px', cursor:'pointer', fontSize:12,
                                borderBottom:'1px solid var(--bd)' }}>
                    <b>{t.ticker}</b>
                    <span style={{ color:'var(--t2)', marginLeft:8 }}>{t.stock_name}</span>
                  </div>
                ))}
              </div>
            )}</div>
          <div><label style={lbl}>Headline</label>
            <input style={inp} value={f.headline} placeholder="What happened"
                   onChange={e => set('headline', e.target.value)} /></div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginBottom:10 }}>
          <div><label style={lbl}>Sentiment</label>
            <select style={inp} value={f.sentiment} onChange={e => set('sentiment', e.target.value)}>
              {(meta?.sentiments || []).map(x => <option key={x}>{x}</option>)}
            </select></div>
          <div><label style={lbl}>Category</label>
            <select style={inp} value={f.category} onChange={e => set('category', e.target.value)}>
              {(meta?.categories || []).map(x => <option key={x}>{x}</option>)}
            </select></div>
          <div><label style={lbl}>Impact</label>
            <select style={inp} value={f.impact} onChange={e => set('impact', e.target.value)}>
              {(meta?.impacts || []).map(x => <option key={x}>{x}</option>)}
            </select></div>
          <div><label style={lbl}>Source</label>
            <input style={inp} value={f.source} placeholder="Moneycontrol"
                   onChange={e => set('source', e.target.value)} /></div>
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={lbl}>Detail (optional)</label>
          <textarea style={{ ...inp, minHeight: 54, fontFamily: 'inherit' }} value={f.detail}
                    onChange={e => set('detail', e.target.value)} />
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button onClick={submit} disabled={saving} style={{
            padding:'7px 20px', fontSize:12, fontWeight:600,
            cursor: saving ? 'wait' : 'pointer',
            borderRadius:6, border:'none',
            background: saving ? '#8c8c8c' : '#0050b3', color:'#fff'
          }}>{saving ? 'Saving...' : 'Save'}</button>
          <span style={{ fontSize:11, color:'var(--t2)' }}>
            Expiry set automatically by category ({meta?.default_expiry_days?.[f.category] ?? 14} days)
          </span>
        </div>
        {msg && (
          <div style={{
            marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            background: msg.ok ? '#f6ffed' : '#fff1f0',
            border: `1px solid ${msg.ok ? '#b7eb8f' : '#ffa39e'}`,
            color: msg.ok ? C.pos : C.neg
          }}>{msg.ok ? '\u2713 ' : '\u2717 '}{msg.text}</div>
        )}
      </div>

      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>
        Recent entries ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--t2)' }}>Nothing entered yet.</div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead><tr style={{ fontSize:10, color:'var(--t2)', textTransform:'uppercase' }}>
              {['Date','Ticker','Sentiment','Category','Impact','Headline','Expires',''].map(h =>
                <th key={h} style={{ textAlign:'left', padding:'5px 8px',
                                     borderBottom:'1px solid var(--bd)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom:'1px solid var(--bd)',
                                        opacity: r.expired ? 0.45 : 1 }}>
                  <td style={{ padding:'5px 8px', color:'var(--t2)' }}>{r.news_date}</td>
                  <td style={{ padding:'5px 8px', fontWeight:700 }}>{r.ticker}</td>
                  <td style={{ padding:'5px 8px', color:sColor(r.sentiment), fontWeight:600 }}>
                    {r.sentiment}</td>
                  <td style={{ padding:'5px 8px', fontSize:10, color:'var(--t2)' }}>{r.category}</td>
                  <td style={{ padding:'5px 8px', fontSize:10 }}>{r.impact}</td>
                  <td style={{ padding:'5px 8px' }} title={r.detail || ''}>{r.headline}</td>
                  <td style={{ padding:'5px 8px', fontSize:10, color: r.expired ? C.neg : 'var(--t2)' }}>
                    {r.expires_on}{r.expired ? ' (expired)' : ''}</td>
                  <td style={{ padding:'5px 8px' }}>
                    <button onClick={() => del(r.id)} style={{
                      fontSize:10, padding:'2px 8px', cursor:'pointer', borderRadius:4,
                      border:'1px solid var(--bd)', background:'transparent', color:C.neg
                    }}>del</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewsManual;
